/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { PhoneOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import cn from 'classnames';
import { FieldInputProps, FieldMetaProps } from 'formik';
import React, { HTMLProps, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Text } from '~/components/Typo/Typo';
import CustomTextField from '~/components/fields/CustomTextField/CustomTextField';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { FontWeight } from '~/theme/constants';
// import CountrySelect from '~/components/forms/CountrySelect/CountrySelect';
import mtnMoneyLogo from '~/assets/images/mtnmoney-logo.png';
import orangeMoneyLogo from '~/assets/images/orangemoney-logo.png';
import Image from '~/components/Image/Image';
import Loader from '~/components/Loader/Loader';
import { ActiveOperator } from '~/entities/schemas/activeOperator';
import { AvailableCountry } from '~/entities/schemas/availableCountry';
import { MeansOfPayment } from '~/entities/schemas/meansOfPayment';
import { logger } from '~/utils/func/logger';
import { formatNumberAuthorizeByCountry } from '~/utils/handle-input-events';

import './MobilePaymentForm.scss';

export interface MobilePaymentFormProps extends HTMLProps<HTMLFormElement> {
  // handleSubmit: (values: any, payment: MeansOfPayment) => void;
  selectedItem?: number;
  paymentInfos?: GetPaymentResponse;
  loading?: boolean;
  getFieldProps: (name: string) => FieldInputProps<any>;
  getFieldMeta: (name: string) => FieldMetaProps<any>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: any;
  valuesMobile: any;
  gettingCountries: boolean;
  countries: AvailableCountry[];
  countriesRefact: string[];
  setSelectedAvailableCountry: any;
  setPhoneNumber: any;
  setDialCode: any;
  isMobile?: boolean;
  onMobileFormActivated?: () => void;
}

const MobilePaymentForm: React.FC<MobilePaymentFormProps> = ({
  paymentInfos,
  loading,
  getFieldProps,
  getFieldMeta,
  handleChange,
  setFieldValue,
  valuesMobile,
  gettingCountries,
  countries,
  countriesRefact,
  setSelectedAvailableCountry,
  setPhoneNumber,
  setDialCode,
  isMobile,
  onMobileFormActivated,
}) => {
  const { t } = useTranslation();

  const [phone, setPhone] = useState<string>('');
  const [meanOfPayment, setMeanOfPayment] = useState<MeansOfPayment>();
  const [meanOfPayments, setMeanOfPayments] = useState<MeansOfPayment[]>([]);
  const [loader, setLoader] = useState<boolean>(true);
  const [operators, setOperators] = useState<ActiveOperator[]>([]);
  const [availableCountry, setAvailableCountry] = useState<AvailableCountry>();
  const [countrySelected, setCountrySelected] = useState<boolean>(false);
  // const [countriesRefact, setCountriesRefact] = useState<string[]>([]);
  // const [refactoring, setRefactoring] = useState<boolean>(false);

  // Handler pour le champ phoneNumber qui met à jour Formik ET OrderDetails
  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e); // Met à jour Formik

      // Utiliser l'indicatif du formulaire plutôt que localStorage pour éviter les problèmes de timing
      const currentDialCode = valuesMobile.indicatif || localStorage.getItem('countrySelectedOne') || '';
      const numberOnly = e.target.value;
      const fullPhoneNumber = currentDialCode + numberOnly;

      logger.debug('DEBUG handlePhoneNumberChange', {
        prefix: 'MobilePayment',
        data: {
          currentDialCode,
          numberOnly,
          fullPhoneNumber,
          formIndicatif: valuesMobile.indicatif,
          localStorage: localStorage.getItem('countrySelectedOne'),
        },
      });

      // S'assurer que dialCode et phoneNumber sont synchronisés
      setDialCode(currentDialCode);
      setPhoneNumber(fullPhoneNumber);
    },
    [handleChange, setPhoneNumber, setDialCode, valuesMobile.indicatif],
  );

  // Applique la sélection d'un pays (opérateurs, indicatif, numéro complet)
  const applyCountrySelection = useCallback(
    (indicatif: string) => {
      setCountrySelected(true);
      localStorage.setItem('countrySelectedOne', indicatif);

      const countryMapping: { [key: string]: string } = {
        225: 'ci', // Côte d'Ivoire
        237: 'cm', // Cameroun
        // Pays commentés par l'utilisateur
        // 242: 'cg', // Congo
        // 235: 'td', // Tchad
        // 236: 'cf', // RCA
        // 241: 'ga', // Gabon
      };

      const countryCode = countryMapping[indicatif];
      if (countryCode) {
        const activeCountry = countries.find(c => c.countrycode.toLowerCase() === countryCode);
        if (activeCountry) {
          setSelectedAvailableCountry(activeCountry);
          setOperators(activeCountry.operators as ActiveOperator[]);
          setAvailableCountry(activeCountry);
        }
      }
      // Met à jour OrderDetails pour le bouton Pay (pour tous les indicatifs valides)
      setDialCode(indicatif);

      // Reconstruire le numéro complet avec le nouvel indicatif
      const currentNumber = valuesMobile.phoneNumber || '';
      setPhoneNumber(indicatif + currentNumber);
    },
    [countries, setSelectedAvailableCountry, setDialCode, setPhoneNumber, valuesMobile.phoneNumber],
  );

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const indicatif = e.target.value;
      handleChange(e);

      if (indicatif === 'Choisir' || indicatif === '') {
        setCountrySelected(false);
        setOperators([]);
        setAvailableCountry(undefined);
        setSelectedAvailableCountry(undefined);
      } else {
        // Déclencher la sélection de l'option Mobile Money dans l'accordéon seulement si nécessaire
        logger.debug('Mobile Money - Indicatif sélectionné', {
          prefix: 'MobilePayment',
          data: { indicatif, isMobile },
        });
        if (onMobileFormActivated && !isMobile) {
          logger.debug('Activation Mobile Money depuis sélection indicatif', { prefix: 'MobilePayment' });
          onMobileFormActivated();
        }

        applyCountrySelection(indicatif);
      }
    },
    [handleChange, setSelectedAvailableCountry, isMobile, onMobileFormActivated, applyCountrySelection],
  );

  const validate = Yup.object({
    // phone: Yup.string().required(t('home:cardPaymentForm:phoneRequired')),
    // fullName: Yup.string().required(t('home:cardPaymentForm:fullNameRequired')),
  });

  const setDefaultOperator = useCallback(() => {
    // Ne plus sélectionner automatiquement un pays - forcer l'utilisateur à choisir
    setOperators([]);
    setAvailableCountry(undefined);
    setSelectedAvailableCountry(undefined);
    setCountrySelected(false);

    // Réinitialiser les valeurs
    setFieldValue('indicatif', '');
    setDialCode('');
    setPhoneNumber('');

    // Nettoyer localStorage si nécessaire
    localStorage.removeItem('countrySelectedOne');
  }, [setFieldValue, setDialCode, setPhoneNumber]);

  const initData = useCallback(() => {
    setLoader(true);
    const mobileMeansOfPayment = paymentInfos?.data.meansOfPayment.filter(
      d => d.type === 'MOBILE_MONEY',
    ) as MeansOfPayment[];
    setMeanOfPayments(mobileMeansOfPayment);
    if (paymentInfos !== undefined) {
      setLoader(false);
    }
  }, [paymentInfos]);

  // const initCountries = useCallback(() => {
  //   setRefactoring(true);
  //   const finalCountries = [];
  //   for (const c of countries) {
  //     finalCountries.push(c.countrycode.toLowerCase());
  //   }
  //   console.log('finalCountries', finalCountries);
  //   setCountriesRefact(finalCountries);
  //   setRefactoring(false);
  // }, [countries]);

  const getImageOnCodeBased = useCallback((code: string): string => {
    switch (code) {
      case 'MOMO.CM':
        return mtnMoneyLogo;
      case 'OM.CM':
        return orangeMoneyLogo;
      default:
        return ''; // TODO: add a default image
    }
  }, []);

  const isCountryCodeAllowed = (countryCode: string) =>
    [paymentInfos?.data.customerCountry ? paymentInfos?.data.customerCountry.toLowerCase() : 'cm'].includes(
      countryCode,
    );

  useEffect(() => {
    // initCountries();
    initData();
    // Ne réinitialise que s'il n'y a pas d'indicatif pré-rempli (customerZipCode)
    if (!paymentInfos?.data?.customerZipCode) {
      setDefaultOperator();
    }
  }, [paymentInfos]);

  // Applique les effets de la sélection quand l'indicatif est pré-rempli
  // (Formik le renseigne via initialValues une fois paymentInfos chargé)
  useEffect(() => {
    if (valuesMobile.indicatif && countries.length > 0 && !countrySelected) {
      applyCountrySelection(valuesMobile.indicatif);
    }
  }, [valuesMobile.indicatif, countries, countrySelected, applyCountrySelection]);
  return (
    <div className="mobile-payment-form">
      <div className="caption-wrapper">
        <div className="caption-container">
          <Text className="caption" weight={FontWeight.BOLD}>
            {t('home:mobilePaymentForm:availableOperators')}
          </Text>
        </div>
        {isMobile && loading && (
          <div className="sub-caption-container">
            <Text className="caption" weight={FontWeight.NORMAL}>
              {t('home:mobilePaymentForm:yourPaymentIsBeingProcessed')}
            </Text>
          </div>
        )}
      </div>
      <div className="mean-of-payment-wrapper">
        <Row justify="center">
          {operators.length > 0 ? (
            operators.map((e, i) => (
              <Col lg={3} md={3} sm={6} xs={6}>
                {/* <Card key={i} className="only-payment"> */}
                {/* {e.network} */}
                <Image className="logo-wrapper" imgClassName="logo" image={e.logo_url} rounded />
                {/* </Card> */}
              </Col>
            ))
          ) : (
            <Text>
              {countrySelected === false
                ? t('expressUnion:countrySelect')
                : t('home:mobilePaymentForm:noOperatorsAreAvailable')}
            </Text>
          )}
        </Row>
      </div>
      <div></div>
      {/* <div className="subtitle-wrapper">
        <Text className="subtitle" size="p" weight={FontWeight.BOLD}>
          Moyens de paiement disponibles
        </Text>
      </div> */}
      {/* {loader ? (
        <Row justify="center">
          <Loader />
        </Row>
      ) : (
        <Row justify="center">
          {meanOfPayments?.map((e, i) => (
            <Col key={i} lg={10} xs={8} sm={10} md={10}>
              <MobilePaymentCard
                onClick={() => {
                  setMeanOfPayment(e);
                }}
                active={e.id === meanOfPayment?.id}
                key={i.toString()}
                label={e.code}
                image={getImageOnCodeBased(e.code)}
              />
            </Col>
          ))}
        </Row>
      )} */}
      <div className="mobile-form-wrapper">
        {gettingCountries ? (
          <Row justify="center">
            <Loader />
          </Row>
        ) : (
          <Row gutter={[30, 30]} style={{ backgroundColor: 'transparent', width: '60%', margin: '0 auto' }}>
            <Col span={24} md={7} className="pad">
              <select
                className={cn(
                  'option-field suffix-icon-wrapper',
                  getFieldMeta('indicatif').error ? 'select2 option-field suffix-icon-wrapper' : '',
                )}
                name="indicatif"
                onChange={handleCountryChange}
                value={valuesMobile.indicatif || ''}
                disabled={loading}
              >
                <option value="">Choisir</option>
                <option value="225">(+225) 🇨🇮</option>
                <option value="237">(+237) 🇨🇲</option>
                {/* <option value="242">(+242) 🇨🇬</option>
                <option value="235">(+235) 🇹🇩</option>
                <option value="236">(+236) 🇨🇫</option>
                <option value="241">(+241) 🇬🇦</option> */}
              </select>
              <div className="text-danger">
                {getFieldMeta('indicatif').error && t('expressUnion:indicatifExpressUnionRequired')}
              </div>
            </Col>
            <Col span={24} md={17} className="pad">
              {countrySelected === true ? (
                <>
                  <CustomTextField
                    name="phoneNumber"
                    type="text"
                    label={''}
                    placeholder="xxxxxxxxxx"
                    meta={getFieldMeta('phoneNumber')}
                    field={getFieldProps('phoneNumber')}
                    onChange={handlePhoneNumberChange}
                    onInput={formatNumberAuthorizeByCountry}
                    inputMode="numeric"
                    suffixIcon={<PhoneOutlined className="suffix-icon" />}
                    required
                    disabled={loading}
                  />
                </>
              ) : (
                <div id="dcenter">
                  <p>{t('expressUnion:countrySelect')}</p>
                </div>
              )}
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default MobilePaymentForm;
